import { useState } from 'react';
import { MicrophoneIcon, StopIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { useVoiceRecorder } from '../../../hooks/useVoiceRecorder';
import type { EnvoiMessage, ReponseRapide } from '../../api/useMessagesMenage';
import BarreSaisie from '../../components/BarreSaisie';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import Text from '../../components/Text';
import { t } from '../../i18n/langue';

import FeuilleOutilsMenage from './FeuilleOutilsMenage';

export interface ReponseEnCours {
    messageId: number;
    prive: boolean;
}

interface BarreMenageProps {
    onEnvoyer: (envoi: Omit<EnvoiMessage, 'filId'>) => Promise<unknown>;
    enVol: boolean;
    chambres: { id: number; name: string }[];
    peutMarquerUrgent: boolean;
    reponse: ReponseEnCours | null;
    onAnnulerReponse: () => void;
}

/**
 * Le pied d'un fil. Le MICRO est l'action principale (agentes qui lisent mal,
 * Q9) : un appui pour parler, un appui pour envoyer. Le texte reste sous la main,
 * les autres gestes sous le « + ».
 */
export default function BarreMenage({ onEnvoyer, enVol, chambres, peutMarquerUrgent, reponse, onAnnulerReponse }: BarreMenageProps) {
    const enregistreur = useVoiceRecorder(120);
    const [texte, setTexte] = useState('');
    const [outils, setOutils] = useState(false);
    const [chambreId, setChambreId] = useState<number | null>(null);
    const [urgent, setUrgent] = useState(false);
    const [echec, setEchec] = useState(false);

    const envoyer = async (envoi: Omit<EnvoiMessage, 'filId'>) => {
        setEchec(false);
        try {
            await onEnvoyer({
                room_id: chambreId,
                is_urgent: urgent,
                reply_to_message_id: reponse?.messageId ?? null,
                private: reponse?.prive ?? false,
                ...envoi,
            });
            setChambreId(null);
            setUrgent(false);
            onAnnulerReponse();

            return true;
        } catch {
            setEchec(true);

            return false;
        }
    };

    const handleMicro = async () => {
        if (!enregistreur.isRecording) {
            enregistreur.toggle();

            return;
        }
        const audio = await enregistreur.stop();
        if (audio && (await envoyer({ audio, body: texte }))) {
            setTexte('');
            enregistreur.clear();
        }
    };

    const handleTexte = async () => {
        if (texte.trim() !== '' && (await envoyer({ body: texte }))) setTexte('');
    };

    const handleRapide = (cle: ReponseRapide) => {
        setOutils(false);
        void envoyer({ quick_reply_key: cle });
    };

    const handlePhoto = async (photo: File) => {
        setOutils(false);
        if (await envoyer({ photo, body: texte })) setTexte('');
    };

    return (
        <div className="flex flex-col gap-2 border-t border-border-color-light bg-white px-3 pb-2 pt-2">
            {reponse && (
                <div className="flex items-center justify-between gap-2 rounded-xl bg-main-color-light px-3 py-1">
                    <Text variante="petit" ton="marque" as="span">
                        {reponse.prive ? t('messagesMenage.barre.reponseEnPrive') : t('messagesMenage.barre.reponseATous')}
                    </Text>
                    <IconButton libelle={t('messagesMenage.barre.annulerReponse')} onPress={onAnnulerReponse}>
                        <XMarkIcon className="h-5 w-5 text-text-color" />
                    </IconButton>
                </div>
            )}
            {(urgent || chambreId !== null) && (
                <Text variante="petit" ton={urgent ? 'danger' : 'marque'}>
                    {[urgent ? `🔴 ${t('messagesMenage.barre.urgent')}` : null, chambres.find((c) => c.id === chambreId)?.name]
                        .filter(Boolean)
                        .join(' · ')}
                </Text>
            )}
            {echec && (
                <div role="alert">
                    <Text variante="petit" ton="danger">
                        {t('messagesMenage.barre.echec')}
                    </Text>
                </div>
            )}
            {enregistreur.error === 'denied' && (
                <Text variante="petit" ton="danger">
                    {t('messagesMenage.barre.micRefuse')}
                </Text>
            )}

            {enregistreur.isSupported && (
                <Button
                    libelle={
                        enregistreur.isRecording
                            ? `${t('messagesMenage.barre.enregistrement', { secondes: enregistreur.seconds })} · ${t('messagesMenage.barre.arreter')}`
                            : t('messagesMenage.barre.parler')
                    }
                    icone={enregistreur.isRecording ? StopIcon : MicrophoneIcon}
                    variante={enregistreur.isRecording ? 'danger' : 'primaire'}
                    pleineLargeur
                    disabled={enVol}
                    onPress={() => void handleMicro()}
                />
            )}

            <BarreSaisie
                valeur={texte}
                onChange={setTexte}
                onEnvoyer={() => void handleTexte()}
                onOuvrirOutils={() => setOutils(true)}
                enVol={enVol}
                libelles={{
                    champ: t('messagesMenage.barre.champ'),
                    placeholder: t('messagesMenage.barre.champ'),
                    envoyer: t('messagesMenage.barre.envoyer'),
                    outils: t('messagesMenage.barre.reponsesRapides'),
                }}
            />

            {outils && (
                <FeuilleOutilsMenage
                    onFermer={() => setOutils(false)}
                    onReponseRapide={handleRapide}
                    onPhoto={(photo) => void handlePhoto(photo)}
                    chambres={chambres}
                    chambreId={chambreId}
                    onChambre={setChambreId}
                    urgent={peutMarquerUrgent ? { valeur: urgent, onChange: setUrgent } : undefined}
                />
            )}
        </div>
    );
}
